import { Earning } from './earnings'
import { Compensation } from './compensation'
import { DriverMaster } from './driverMaster'
import { Platform } from './index'
import { SalaryCalculationConfig, SalaryCalculationItem } from '@/services/salaryCalculationService'

/**
 * Snapshot of driver information for salary calculation
 * Contains only the essential fields needed for PDF generation and audit trail
 */
export interface SalaryDriverInfo {
  id: string
  name: string
  employeeNumber?: string
  email?: string
  personalCode?: string
  bankAccount?: string
}

/**
 * Raw earning data from platform
 * Preserves source data for audit trail and verification
 */
export interface SalaryRawEarning {
  earningsId: string
  platform: Platform
  grossEarnings: number
  netEarnings: number
  tips: number
  cashReceived?: number
  uploadSessionId: string
}

/**
 * Driver-specific tax settings at the time of snapshot creation
 * These override fleet-wide settings for individual drivers
 */
export interface SalaryDriverTaxSettings {
  taxCalculationMethod?: 'fixed' | 'table'
  taxRate?: number              // Percentage (0-100)
  taxTableName?: string          // e.g., "31" or "skattetabell31"
  taxColumn?: number             // 1-6
  salaryGarnishment?: number     // Löneutmätning amount
}

/**
 * Aggregated salary totals for a driver in a period
 * All calculations are done through SalaryCalculationService
 */
export interface SalaryAggregatedTotals {
  // Earnings totals
  grossEarnings: number
  netEarnings: number
  tips: number
  cashReceived: number

  // Calculation breakdown
  vatAmount: number
  shareableAmount: number
  afterFleetCommission: number
  vacationPay: number

  // Applied driver revenue share rate (percentage 0-100)
  // This is the actual rate used, which may differ from settings when tiered mode is active
  appliedDriverRevenueShare: number

  // Salary before tax
  grossSalary: number
  finalGrossSalary: number

  // Employer costs
  employerTaxAmount: number
  totalEmployerCost: number

  // Salary after tax
  netSalary: number
  finalNetSalary: number
  taxAmount: number
  effectiveTaxRate: number      // Percentage (0-100)

  // Tips handling
  untaxedTips: number

  // The actual amount paid out to the driver ("Att utbetala"):
  // finalNetSalary + untaxedTips - cashReceived
  amountToPay: number

  // Compensations summary
  netEarningsCompensations: number
  netEarningsDeductions: number
  grossSalaryCompensations: number
  grossSalaryDeductions: number
  netSalaryCompensations: number
  netSalaryDeductions: number
}

/**
 * Complete salary snapshot for a driver in a specific period
 * This is the unified data structure used for:
 * - Preview calculations (not saved to Firestore)
 * - Saved salary snapshots (stored in Firestore)
 * - PDF generation
 *
 * Benefits:
 * - Single source of truth for salary data
 * - No discrepancies between preview and saved data
 * - Reusable across all salary-related features
 */
export interface CompleteSalarySnapshot {
  // Identification
  driverId: string
  period: string                 // Format: "yyyy-mm-01"

  // Driver information
  driverInfo: SalaryDriverInfo

  // Raw source data - preserves original platform data
  rawEarnings: SalaryRawEarning[]

  // Detailed calculations for each earning
  calculatedEarnings: SalaryCalculationItem[]

  // Compensations applied
  compensations: Compensation[]

  // Configuration used for calculations
  fleetSettings: SalaryCalculationConfig
  driverTaxSettings?: SalaryDriverTaxSettings

  // Aggregated totals
  totals: SalaryAggregatedTotals

  // Metadata (optional, only for saved snapshots)
  id?: string                    // Firestore document ID
  createdAt?: Date
  isActive?: boolean
}

/**
 * Convert DriverMaster to SalaryDriverInfo
 */
export function convertDriverToInfo(driver: DriverMaster): SalaryDriverInfo {
  return {
    id: driver.id,
    name: driver.primaryName,
    employeeNumber: driver.employeeNumber,
    email: driver.email,
    personalCode: driver.personalCode,
    bankAccount: driver.bankAccount
  }
}

/**
 * Convert Earning to SalaryRawEarning
 * Ensures no undefined values are passed to Firestore
 */
export function convertEarningToRaw(earning: Earning): SalaryRawEarning {
  return {
    earningsId: earning.id,
    platform: earning.platform,
    grossEarnings: earning.grossEarnings,
    netEarnings: earning.netEarnings,
    tips: earning.tips ?? 0,
    ...(earning.cashReceived !== undefined && { cashReceived: earning.cashReceived }),
    uploadSessionId: earning.uploadSessionId
  }
}
